import { lazy, Suspense } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { findNavItem } from '@/shared/patterns/AppShell';
import { UnderConstruction } from '@/shared/components/UnderConstruction';
import { Loading } from '@/kit';
import { RequirePermissions } from '@/shared/components/RequirePermissions';
// El nav NO va lazy: el AppShell lo necesita para pintar el sidebar en cuanto entra al portal.
import { retailAdminNav } from '@/domains/retail';

/**
 * Las pantallas del portal se cargan bajo demanda.
 *
 * Son vistas densas —tablas, editores, gráficas— de las que un operador abre una o dos por
 * sesión. Empaquetarlas en el bundle inicial hacía que quien entra a ver un cliente descargara
 * también el editor del plan de comisiones y la matriz de análisis.
 *
 * Cada `lazy` es un chunk propio, así que agregar la pantalla 13 no engorda el arranque.
 */
const ClientesPage = lazy(() =>
    import('@/domains/retail/admin/clientes/ClientesPage').then((m) => ({
        default: m.ClientesPage,
    })),
);
const InventarioPage = lazy(() =>
    import('@/domains/retail/admin/inventario/InventarioPage').then((m) => ({
        default: m.InventarioPage,
    })),
);
const TiendasPage = lazy(() =>
    import('@/domains/retail/admin/tiendas/TiendasPage').then((m) => ({ default: m.TiendasPage })),
);
const VendedoresPage = lazy(() =>
    import('@/domains/retail/admin/vendedores/VendedoresPage').then((m) => ({
        default: m.VendedoresPage,
    })),
);
const ReportesPage = lazy(() =>
    import('@/domains/retail/admin/reportes/ReportesPage').then((m) => ({
        default: m.ReportesPage,
    })),
);
const ExcepcionesPage = lazy(() =>
    import('@/domains/retail/admin/excepciones/ExcepcionesPage').then((m) => ({
        default: m.ExcepcionesPage,
    })),
);
const AnalisisPage = lazy(() =>
    import('@/domains/retail/admin/analisis/AnalisisPage').then((m) => ({
        default: m.AnalisisPage,
    })),
);
const ConciliacionPage = lazy(() =>
    import('@/domains/retail/admin/conciliacion/ConciliacionPage').then((m) => ({
        default: m.ConciliacionPage,
    })),
);
const PlanComisionesPage = lazy(() =>
    import('@/domains/retail/admin/comisiones/PlanComisionesPage').then((m) => ({
        default: m.PlanComisionesPage,
    })),
);
const ComisionesPagarPage = lazy(() =>
    import('@/domains/retail/admin/comisiones/ComisionesPagarPage').then((m) => ({
        default: m.ComisionesPagarPage,
    })),
);
const PromotionsPage = lazy(() =>
    import('@/domains/retail/admin/promotions/PromotionsPage').then((m) => ({
        default: m.PromotionsPage,
    })),
);
const TransferNotesRetailerPage = lazy(() =>
    import('@/domains/transferNotes/TransferNotesRetailerPage').then((m) => ({
        default: m.TransferNotesRetailerPage,
    })),
);
const VentasPage = lazy(() =>
    import('@/domains/retail/ventas/VentasPage').then((m) => ({ default: m.VentasPage })),
);

/**
 * Search params compartidos por las secciones del portal — los deep-links de los mockups.
 *
 * `tab` es `string` y no un enum a propósito: cada sección tiene sus propias pestañas (Clientes
 * usa resumen/compras/financieras/tiendas, Inventario usa critico/sin-rotacion/gap/traspasos) y
 * un enum acá obligaría a editar la ruta cada vez que nace una pantalla. **Cada pantalla valida el
 * suyo** y cae a su default si no reconoce el valor — así una URL vieja no revienta la ruta.
 *
 * Los demás (`cliente`, `tienda`, `vendedor`, `venta`, `exc`) son selección de fila: abren el
 * detalle o el drawer correspondiente. Van en la URL para que una fila sea compartible por link.
 * `periodo` es la banda de periodo; cada pantalla que la usa valida el valor.
 */
const searchSchema = z.object({
    tab: z.string().optional(),
    cliente: z.string().optional(),
    tienda: z.string().optional(),
    vendedor: z.string().optional(),
    venta: z.string().optional(),
    exc: z.string().optional(),
    reporte: z.string().optional(),
    cadena: z.string().optional(),
    periodo: z.coerce.string().optional(),
});

/** Los permisos que el menú pide para la sección; la ruta exige los mismos. */
function navPermissions(slug: string): readonly string[] {
    const permission = findNavItem(retailAdminNav, slug)?.permission;
    if (permission === undefined) return [];
    return typeof permission === 'string' ? [permission] : permission;
}

// Cada sección del portal despacha a su pantalla; las que aún no existen caen a UnderConstruction.
export const Route = createFileRoute('/_backoffice/retail-admin/$section')({
    validateSearch: (search: Record<string, unknown>) => searchSchema.parse(search),
    component: RetailAdminSection,
});

function RetailAdminSection() {
    const { section } = Route.useParams();
    const { tab, tienda, vendedor, cadena, venta } = Route.useSearch();
    // Un solo `Suspense` alrededor del switch: el fallback es el mismo para todas y el AppShell
    // ya pintó el sidebar, así que la espera se siente como una pantalla cargando, no como un salto.
    return (
        <Suspense fallback={<Loading fullScreen={false} className="py-16" />}>
            {renderSection()}
        </Suspense>
    );

    function renderSection() {
        switch (section) {
            case 'clientes':
                return <ClientesPage />;
            case 'inventario':
                return <InventarioPage key={tab ?? ''} tab={tab} />;
            case 'tiendas':
                return <TiendasPage />;
            case 'vendedores':
                return <VendedoresPage />;
            case 'reportes':
                return <ReportesPage />;
            case 'excepciones':
                return <ExcepcionesPage />;
            case 'analisis':
                return <AnalisisPage />;
            case 'conciliacion':
                // Oculta en el menú; por URL directa se exige el permiso del item.
                return (
                    <RequirePermissions permissions={navPermissions('conciliacion')}>
                        <ConciliacionPage />
                    </RequirePermissions>
                );
            case 'plan-comisiones':
                return <PlanComisionesPage />;
            case 'comisiones':
                return <ComisionesPagarPage />;
            case 'promociones':
                return <PromotionsPage />;
            case 'transferencias':
                return <TransferNotesRetailerPage />;
            case 'ventas':
                // La pantalla de Ventas ya existía (construida en otra fase sobre el MISMO mockup);
                // acá solo se monta en el portal y se le siembran los filtros que traiga la URL.
                // La `key` la remonta cuando cambia el deep link: sin eso, llegar desde otra tienda
                // reusaría el estado de la anterior y el filtro nuevo se perdería.
                return (
                    <VentasPage
                        key={`${cadena ?? ''}|${tienda ?? ''}|${vendedor ?? ''}|${venta ?? ''}`}
                        initialSaleId={venta}
                        scopeToOwnRetailer
                        initialFilters={{
                            retailerId: cadena ?? '',
                            storeId: tienda,
                            sellerId: vendedor,
                        }}
                    />
                );
            default:
                return (
                    <UnderConstruction
                        title={findNavItem(retailAdminNav, section)?.label ?? section}
                    />
                );
        }
    }
}
